import { useEffect, useState } from "react";
import { adminFetch } from "./adminApi";

const FILTERS = ["PENDING", "AUTO_APPROVED", "APPROVED", "REJECTED", "ALL"];

const STATUS_COLORS = {
  PENDING: "text-yellow-300 bg-yellow-500/15 border-yellow-400/30",
  AUTO_APPROVED: "text-green-300 bg-green-500/15 border-green-400/30",
  APPROVED: "text-green-300 bg-green-500/15 border-green-400/30",
  REJECTED: "text-red-300 bg-red-500/15 border-red-400/30",
};

const DECISION_COLORS = {
  AUTO_APPROVED: "text-green-300",
  AUTO_REJECTED: "text-red-300",
  MANUAL_REVIEW: "text-yellow-300",
};

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
}

function EvidenceRow({ label, value, highlight }) {
  return (
    <div className="flex justify-between items-center py-1.5 border-b border-white/5 last:border-0">
      <span className="text-xs text-white/55">{label}</span>
      <span className={`text-xs font-semibold ${highlight || "text-white/90"}`}>{value}</span>
    </div>
  );
}

export default function AdminRequestDisputes() {
  const [statusFilter, setStatusFilter] = useState("PENDING");
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [selected, setSelected] = useState(null);
  const [adminNote, setAdminNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionErr, setActionErr] = useState("");

  const pageSize = 20;

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const params = new URLSearchParams({ page, pageSize });
      if (statusFilter !== "ALL") params.set("status", statusFilter);
      const data = await adminFetch(`/api/admin/request-disputes?${params}`);
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotal(Number(data?.total || 0));
    } catch (e) {
      setErr(e?.message || "Failed to load disputes");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [statusFilter, page]); // eslint-disable-line

  function openDetail(item) {
    setSelected(item);
    setAdminNote("");
    setActionErr("");
  }

  async function approve() {
    if (!selected) return;
    setBusy(true);
    setActionErr("");
    try {
      const res = await adminFetch(`/api/admin/request-disputes/${selected.id}/approve`, {
        method: "POST",
        body: { adminNote },
      });
      setSelected(null);
      load();
      if (res?.refunded > 0) {
        // small visual feedback
        console.info(`Refunded ${res.refunded} credits`);
      }
    } catch (e) {
      setActionErr(e?.message || "Failed to approve");
    } finally {
      setBusy(false);
    }
  }

  async function reject() {
    if (!selected) return;
    if (!adminNote.trim()) {
      setActionErr("Admin note is required to reject");
      return;
    }
    setBusy(true);
    setActionErr("");
    try {
      await adminFetch(`/api/admin/request-disputes/${selected.id}/reject`, {
        method: "POST",
        body: { adminNote },
      });
      setSelected(null);
      load();
    } catch (e) {
      setActionErr(e?.message || "Failed to reject");
    } finally {
      setBusy(false);
    }
  }

  const pages = Math.ceil(total / pageSize);

  // Count pending for badge
  const pendingCount = statusFilter === "PENDING" ? total : null;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold">Request Disputes</h2>
          <p className="text-sm text-white/50">
            Smart refund requests with auto-decision evidence.{" "}
            {total} {statusFilter !== "ALL" ? statusFilter.toLowerCase() : "total"}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((s) => (
            <button
              key={s}
              onClick={() => { setStatusFilter(s); setPage(1); }}
              className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${
                statusFilter === s
                  ? "border-[#7269e3]/60 bg-[#7269e3]/20 text-white"
                  : "border-white/10 bg-white/5 text-white/70 hover:bg-white/10"
              }`}
            >
              {s}
              {s === "PENDING" && pendingCount !== null && pendingCount > 0
                ? ` (${pendingCount})`
                : ""}
            </button>
          ))}
        </div>
      </div>

      {/* Info box for auto-decisions */}
      <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-xs text-white/60 space-y-0.5">
        <div><span className="text-green-300 font-semibold">AUTO_APPROVED</span> — client never opened chat → refund issued automatically</div>
        <div><span className="text-red-300 font-semibold">AUTO_REJECTED</span> — ≥3 client messages (real session) or specialist barely tried → no refund</div>
        <div><span className="text-yellow-300 font-semibold">MANUAL_REVIEW (PENDING)</span> — ambiguous case, needs admin decision</div>
      </div>

      {err && <div className="rounded-xl bg-red-500/10 p-3 text-sm text-red-200">{err}</div>}
      {loading && <div className="py-8 text-center text-white/50">Loading…</div>}

      {!loading && items.length === 0 && (
        <div className="rounded-2xl border border-dashed border-white/10 py-12 text-center text-white/50">
          No disputes found.
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="space-y-2">
          {items.map((d) => (
            <div
              key={d.id}
              className="cursor-pointer rounded-xl border border-white/10 bg-white/5 px-4 py-3 hover:bg-white/[0.08] transition"
              onClick={() => openDetail(d)}
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${STATUS_COLORS[d.status] || "text-white/60 bg-white/5 border-white/15"}`}>
                      {d.status}
                    </span>
                    <span className={`text-xs font-semibold ${DECISION_COLORS[d.autoDecision] || "text-white/60"}`}>
                      {d.autoDecision || "—"}
                    </span>
                    <span className="text-sm font-semibold text-white/90">
                      {d.request?.subject || "—"}
                    </span>
                  </div>
                  <div className="text-xs text-white/55">
                    Tutor: {d.request?.tutorName || "—"} · Student: {d.request?.studentName || "—"}
                  </div>
                  <div className="text-xs text-white/40">
                    Client msgs: <span className="text-white/70">{d.clientMessageCount}</span> ·
                    Specialist msgs: <span className="text-white/70">{d.specialistMessageCount}</span> ·
                    Fee: <span className="text-white/70">{d.request?.priceCredits ?? "?"} pts</span>
                  </div>
                </div>
                <div className="shrink-0 text-xs text-white/40">{fmt(d.createdAt)}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-sm disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-sm text-white/50">Page {page} / {pages}</span>
          <button
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
            disabled={page === pages}
            className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-sm disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}

      {/* Detail modal */}
      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.7)" }}
          onClick={(e) => { if (e.target === e.currentTarget) setSelected(null); }}
        >
          <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#272c39] p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-lg">Dispute Review</h3>
              <button onClick={() => setSelected(null)} className="text-white/50 hover:text-white text-xl leading-none">×</button>
            </div>

            {/* Status badge */}
            <div className="flex items-center gap-2">
              <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${STATUS_COLORS[selected.status] || ""}`}>
                {selected.status}
              </span>
              <span className={`text-sm font-semibold ${DECISION_COLORS[selected.autoDecision] || "text-white/60"}`}>
                {selected.autoDecision}
              </span>
            </div>

            {/* Request info */}
            <div className="rounded-xl bg-white/5 p-4 space-y-1 text-sm">
              <div><span className="text-white/50">Request:</span> {selected.request?.id}</div>
              <div><span className="text-white/50">Subject:</span> {selected.request?.subject || "—"}</div>
              <div><span className="text-white/50">Tutor:</span> {selected.request?.tutorName || "—"}</div>
              <div><span className="text-white/50">Student:</span> {selected.request?.studentName || "—"}</div>
              <div><span className="text-white/50">Fee:</span> {selected.request?.priceCredits ?? "?"} pts</div>
              <div><span className="text-white/50">Filed:</span> {fmt(selected.createdAt)}</div>
            </div>

            {/* Evidence panel */}
            <div className="rounded-xl bg-white/5 p-4">
              <div className="text-xs font-semibold text-white/50 mb-2 uppercase tracking-wider">Evidence</div>
              <EvidenceRow
                label="Client messages sent"
                value={selected.clientMessageCount}
                highlight={selected.clientMessageCount === 0 ? "text-green-300" : selected.clientMessageCount >= 3 ? "text-red-300" : "text-yellow-300"}
              />
              <EvidenceRow
                label="Specialist messages sent"
                value={selected.specialistMessageCount}
                highlight={selected.specialistMessageCount <= 1 ? "text-red-300" : "text-green-300"}
              />
              <EvidenceRow
                label="Client opened chat at"
                value={selected.clientOpenedChatAt ? fmt(selected.clientOpenedChatAt) : "Never opened"}
                highlight={!selected.clientOpenedChatAt ? "text-green-300" : "text-white/90"}
              />
              <EvidenceRow
                label="Client last seen"
                value={selected.clientLastSeenAt ? fmt(selected.clientLastSeenAt) : "—"}
              />
              <EvidenceRow
                label="Request unlocked at"
                value={fmt(selected.request?.unlockedAt)}
              />
            </div>

            {/* Auto-decision explanation */}
            <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-xs text-white/60">
              {selected.autoDecision === "AUTO_APPROVED" && (
                "Client never opened the chat → auto-refund already issued."
              )}
              {selected.autoDecision === "AUTO_REJECTED" && (
                "Meaningful activity detected → refund not warranted automatically."
              )}
              {selected.autoDecision === "MANUAL_REVIEW" && (
                "Ambiguous case (client opened chat but low messages). Review evidence and decide."
              )}
            </div>

            {/* Admin note */}
            {selected.status === "PENDING" && (
              <div>
                <label className="mb-1 block text-sm text-white/70">
                  Admin note {selected.autoDecision === "MANUAL_REVIEW" ? "(required to reject)" : "(optional)"}
                </label>
                <textarea
                  rows={3}
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  maxLength={2000}
                  placeholder="Internal reasoning for your decision…"
                  className="w-full resize-y rounded-xl border border-white/10 bg-white/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7269e3]"
                />
              </div>
            )}

            {selected.adminNote && selected.status !== "PENDING" && (
              <div className="rounded-xl bg-white/5 p-3">
                <div className="text-xs text-white/50 mb-1">Admin note</div>
                <div className="text-sm text-white/80">{selected.adminNote}</div>
                {selected.reviewedBy && (
                  <div className="mt-1 text-xs text-white/40">
                    by {selected.reviewedBy} · {fmt(selected.reviewedAt)}
                  </div>
                )}
              </div>
            )}

            {actionErr && (
              <div className="rounded-xl bg-red-500/10 p-3 text-sm text-red-200">{actionErr}</div>
            )}

            {selected.status === "PENDING" && (
              <div className="flex gap-2 justify-end pt-1">
                <button
                  onClick={() => setSelected(null)}
                  className="rounded-xl border border-white/10 px-4 py-2 text-sm hover:bg-white/5"
                >
                  Cancel
                </button>
                <button
                  onClick={reject}
                  disabled={busy}
                  className="rounded-xl border border-red-400/30 bg-red-500/10 text-red-200 px-4 py-2 text-sm font-semibold hover:bg-red-500/20 disabled:opacity-50"
                >
                  {busy ? "…" : "Reject (no refund)"}
                </button>
                <button
                  onClick={approve}
                  disabled={busy}
                  className="rounded-xl bg-[#98dea3]/20 text-[#98dea3] px-4 py-2 text-sm font-semibold hover:bg-[#98dea3]/30 disabled:opacity-50"
                >
                  {busy ? "…" : `Approve + Refund ${selected.request?.priceCredits ?? "?"} pts`}
                </button>
              </div>
            )}

            {selected.status !== "PENDING" && (
              <div className="flex justify-end">
                <button
                  onClick={() => setSelected(null)}
                  className="rounded-xl border border-white/10 px-4 py-2 text-sm hover:bg-white/5"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
